import { useEffect } from "react";

/**
 * CampsiteSchema — injects `Campground` + `BreadcrumbList` JSON-LD into the
 * document <head> for the current campsite detail page. This is the SEO
 * trick that lets individual campsite pages rank on long-tail searches
 * like "Trevalgan Touring Park reviews" with star ratings, prices and a
 * Maps panel pulled straight from our data.
 *
 *   • Replaces (not appends) the same `<script id="cc-campsite-schema">`
 *     tag on every render so navigating between campsites swaps schemas
 *     cleanly.
 *   • Strips the tag on unmount so listing pages don't keep stale schema.
 *   • Silent no-op while `site` is null (loading state).
 */
const SITE_URL = "https://camp-compass.uk";
const SCRIPT_ID = "cc-campsite-schema";

function buildSchema(site, campsiteId, reviews, avgRating) {
  const url = `${SITE_URL}/campsites/${campsiteId}`;
  const image = (site.images && site.images[0]?.url)
    || site.image
    || `${SITE_URL}/brand/profile-pic-v2-0.jpg`;

  const breadcrumb = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      { "@type": "ListItem", "position": 1, "name": "CampCompass UK", "item": SITE_URL + "/" },
      { "@type": "ListItem", "position": 2, "name": "Browse", "item": SITE_URL + "/browse" },
      ...(site.region
        ? [{ "@type": "ListItem", "position": 3, "name": site.region, "item": `${SITE_URL}/browse?region=${encodeURIComponent(site.region)}` }]
        : []),
      { "@type": "ListItem", "position": site.region ? 4 : 3, "name": site.name, "item": url },
    ],
  };

  // schema.org/Campground — accommodation subtype Google recognises.
  const campground = {
    "@context": "https://schema.org",
    "@type": "Campground",
    "@id": url + "#campground",
    "name": site.name,
    "url": url,
    "description": (site.description || `${site.name} in ${site.region || "the UK"} — listed on CampCompass UK.`).slice(0, 500),
    "image": image,
    ...(typeof site.lat === "number" && typeof site.lng === "number"
      ? {
          "geo": {
            "@type": "GeoCoordinates",
            "latitude": site.lat,
            "longitude": site.lng,
          },
        }
      : {}),
    ...(site.region
      ? {
          "address": {
            "@type": "PostalAddress",
            "addressRegion": site.region,
            "addressCountry": "GB",
          },
        }
      : {}),
    ...(typeof site.price_per_night === "number"
      ? {
          "priceRange": `£${site.price_per_night.toFixed(0)} / night`,
          "makesOffer": {
            "@type": "Offer",
            "price": site.price_per_night.toFixed(2),
            "priceCurrency": "GBP",
            "availability": "https://schema.org/InStock",
            "url": url,
          },
        }
      : {}),
    ...(reviews && reviews.length > 0 && typeof avgRating === "number" && avgRating > 0
      ? {
          "aggregateRating": {
            "@type": "AggregateRating",
            "ratingValue": Number(avgRating.toFixed(2)),
            "reviewCount": reviews.length,
            "bestRating": 5,
            "worstRating": 1,
          },
        }
      : {}),
    ...(Array.isArray(site.facilities) && site.facilities.length > 0
      ? {
          "amenityFeature": site.facilities.slice(0, 12).map((f) => ({
            "@type": "LocationFeatureSpecification",
            "name": String(f).replace(/_/g, " "),
            "value": true,
          })),
        }
      : {}),
    "isAccessibleForFree": false,
    "publicAccess": true,
  };

  return [breadcrumb, campground];
}

export default function CampsiteSchema({ site, campsiteId, reviews = [], avgRating = null }) {
  useEffect(() => {
    if (!site || !campsiteId) return;
    const blocks = buildSchema(site, campsiteId, reviews, avgRating);
    const json = JSON.stringify(blocks);
    let tag = document.getElementById(SCRIPT_ID);
    if (!tag) {
      tag = document.createElement("script");
      tag.id = SCRIPT_ID;
      tag.type = "application/ld+json";
      document.head.appendChild(tag);
    }
    tag.textContent = json;

    // Canonical URL for this specific campsite — keeps Google from
    // treating the preview domain as duplicate content.
    const canonicalId = "cc-campsite-canonical";
    let canon = document.getElementById(canonicalId);
    if (!canon) {
      canon = document.createElement("link");
      canon.id = canonicalId;
      canon.setAttribute("rel", "canonical");
      document.head.appendChild(canon);
    }
    canon.setAttribute("href", `${SITE_URL}/campsites/${campsiteId}`);

    // hreflang alternates — tells Google that the same campsite page
    // exists in multiple languages. Each language becomes its own SEO
    // entry point (ferry-traffic campers from EU search in their own
    // language). Marked via ?lang=xx because the SPA reads i18n state.
    const alternates = ["en", "fr", "de", "es", "nl"];
    const altIds = alternates.map((l) => `cc-campsite-alt-${l}`);
    alternates.forEach((l) => {
      const aId = `cc-campsite-alt-${l}`;
      let link = document.getElementById(aId);
      if (!link) {
        link = document.createElement("link");
        link.id = aId;
        link.setAttribute("rel", "alternate");
        document.head.appendChild(link);
      }
      link.setAttribute("hreflang", l === "en" ? "en-gb" : l);
      link.setAttribute("href", l === "en"
        ? `${SITE_URL}/campsites/${campsiteId}`
        : `${SITE_URL}/campsites/${campsiteId}?lang=${l}`);
    });
    // x-default — fallback when no specific language match
    const defId = "cc-campsite-alt-default";
    let defLink = document.getElementById(defId);
    if (!defLink) {
      defLink = document.createElement("link");
      defLink.id = defId;
      defLink.setAttribute("rel", "alternate");
      document.head.appendChild(defLink);
    }
    defLink.setAttribute("hreflang", "x-default");
    defLink.setAttribute("href", `${SITE_URL}/campsites/${campsiteId}`);

    return () => {
      const t = document.getElementById(SCRIPT_ID);
      if (t) t.remove();
      const c = document.getElementById(canonicalId);
      if (c) c.remove();
      [...altIds, defId].forEach((aId) => {
        const el = document.getElementById(aId);
        if (el) el.remove();
      });
    };
  }, [site, campsiteId, reviews, avgRating]);

  return null;
}
